// Builds the `submissionContext` that submission channels inject onto the root
// schema before compile, for the `uenCompare` keyword to read (CTD-12664).
//
// Contributor precedence mirrors the producer's contributorResolver.declaredOrgIDs()
// (dex-monorepo be/producer/internal/service/contributor_uen.go):
//   1. the Contributor explicitly nominated for this submission (provide-on-behalf)
//   2. the authenticated entity's own organization (self-submission)
//
// The producer additionally resolves a Contributor per participant and rejects a
// request declaring more than one distinct org ID. Neither applies here: CSV
// upload carries a single on-behalf-of selection for the whole batch, so there is
// no per-participant dimension and no ambiguity to reject.
//
// The producer resolves the Contributor's *org ID* and looks the UEN up in its
// own store. The portal mirrors that: it resolves the Contributor's org ID and
// looks the UEN up in the `orgsys` org list. /v1/config is not a usable source —
// the COMPANY_UEN feature flag gates the UEN field there (CTD-12653).

// Placeholder option injected by AdditionalZone for the "not selected" row.
const UNSELECTED_ID = 'no';

/**
 * Looks a Contributor's UEN up in the `orgsys` orgs by org ID — `contributor.id`
 * on both an `on_behalf_of` entry and `identity.organization`. Null when the org
 * is absent or holds no UEN, which keeps `uenCompare` inert.
 */
export function resolveContributorUen(contributor, orgs) {
	if (contributor == null || typeof contributor !== 'object') return null;
	if (typeof contributor.id !== 'string' || contributor.id === '') return null;
	if (!Array.isArray(orgs)) return null;

	const uen = orgs.find((org) => org?.orgId === contributor.id)?.uen;
	return typeof uen === 'string' && uen.trim() !== '' ? uen : null;
}

/**
 * Picks the nominated Contributor out of the `onBehalfOf` selection.
 * The state holds an array, but callers have historically also passed a bare
 * object, so both are accepted. Entries without an id, and the "not selected"
 * placeholder, are skipped.
 */
export function resolveNominatedContributor(onBehalfOf) {
	const candidates = Array.isArray(onBehalfOf) ? onBehalfOf : [onBehalfOf];

	return candidates.find((entry) =>
		entry != null
		&& typeof entry === 'object'
		&& typeof entry.id === 'string'
		&& entry.id !== ''
		&& entry.id !== UNSELECTED_ID
	) ?? null;
}

/**
 * Builds the submission context for one submission.
 * Returns null when no Contributor can be identified at all, so the channel
 * injects nothing and the keyword stays inert.
 *
 * `contributorChoiceOffered`: whether the UI offers a Contributor selection (the
 * Provide On Behalf Of panel). A choice offered but not yet made is a pending
 * selection, not a self-submission, so the context is null (inert) — the
 * authenticated-organisation fallback below applies only when no choice exists.
 *
 * There is deliberately no fallback from the nominated Contributor to the
 * authenticated organisation: the producer resolves one org and looks its UEN up
 * once, and falling back across organisations would compare against the wrong
 * entity. Note that a single-option on-behalf-of list is auto-selected upstream,
 * so a self-submission can arrive here as a nomination; both resolve their UEN
 * the same way, out of `orgs`.
 *
 * `orgs`: the `orgsys` org list (redux `staticData.data.orgs`, hydrated from
 * sessionStorage). Absent or not yet loaded leaves the rule inert.
 */
export function buildSubmissionContext({ identity, onBehalfOf, contributorChoiceOffered = false, orgs } = {}) {
	const nominated = resolveNominatedContributor(onBehalfOf);
	if (nominated == null && contributorChoiceOffered) return null;

	const contributor = nominated ?? identity?.organization ?? null;
	if (contributor == null) return null;

	return { onBehalfOfId: resolveContributorUen(contributor, orgs) };
}

// Submission channels wired for `uenCompare`. A channel absent from the set below
// reaches the producer unchecked by the portal, so add one only deliberately.
export const SUBMISSION_CHANNEL = { CSV: 'csv', JSON: 'json', EFORM: 'eform' };

const UEN_COMPARE_CHANNELS = new Set([
	SUBMISSION_CHANNEL.CSV,
	SUBMISSION_CHANNEL.JSON,
	SUBMISSION_CHANNEL.EFORM,
]);

export function schemaHasUenCompareRule(schema) {
	return Boolean(schema?.uenCompare || schema?.['x-uenCompare']);
}

// Null for a channel the rule does not cover, or when PAYLOAD_UEN_VALIDATION is
// off — the keyword is inert without a context, so this is the whole kill switch.
export function resolveSubmissionContext({ enabled, channel, identity, onBehalfOf, contributorChoiceOffered, orgs } = {}) {
	if (!enabled || !UEN_COMPARE_CHANNELS.has(channel)) return null;
	return buildSubmissionContext({ identity, onBehalfOf, contributorChoiceOffered, orgs });
}

// A payload is validated against the Contributor selected at the time, so a change
// leaves it checked against the wrong entity. It is re-checked against the new
// Contributor rather than discarded, but only where the rule can actually fail.
export function shouldRevalidateRetainedPayload({ enabled, schema, hasPayload } = {}) {
	return Boolean(enabled) && schemaHasUenCompareRule(schema) && Boolean(hasPayload);
}

export default buildSubmissionContext;
